import { create } from "zustand";
import { persist, devtools, StateStorage } from "zustand/middleware";

import {
  Filters,
  SearchParams,
  Shopify,
  AllergenFilter,
  Visibility,
  CategoryParams,
  PrintableRecipe,
  Bundle,
} from "./types";
import { StoreNewCartShopify } from "@util/types";
import { determineBrand } from "@util/theme";

export const defaultScreenWidth = {
  isMobileWidth: false,
  isTabletWidth: false,
  isSmallLaptopWidth: false,
  isLaptopWidth: false,
};

export const allergenFilter = {
  id: "none",
  name: "None",
};

export const defaultSearchParams = {
  searchString: "",
  hasSearched: false,
  globalSearch: false,
};

export const defaultVisibility = {
  loginDrawerVisible: false,
  cartDrawerVisible: false,
};

export const categoryParams = {
  categoryHandle: "",
  categoryName: "",
  hasCategory: false,
};

export const defaultShopify = {
  customer: {
    email: undefined,
    firstName: undefined,
    token: undefined,
    phone: undefined,
  },
  cart: undefined,
};

export const defaultPrintableRecipe = {
  recipe: undefined,
};

export const defaultBundle = {
  id: "",
  name: "",
  price: 0,
  totalProductsAllowed: 0,
  products: [],
};

const dummyStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

interface State {
  filters?: Filters;
  allergenFilter?: AllergenFilter;
  bundles?: Bundle[];
  categoryParams: CategoryParams;
  printableRecipe?: PrintableRecipe | null;
  savedBlogs: string[];
  savedProducts: string[];
  savedRecipes: string[];
  searchParams: SearchParams;
  shopify: Shopify;
  visibility: Visibility;
  newCartShopify?: StoreNewCartShopify;

  openLoginDrawer: () => void;
  openCartDrawer: () => void;
  closeDrawer: () => void;
  setBundles: (bundles: Array<Bundle>) => void;
  setCategoryParams: (categoryParams: CategoryParams) => void;
  setNewCartShopify: (newCartShopify: StoreNewCartShopify) => void;
  getCustomerToken: () => string | undefined;
  getCustomerId: () => string | undefined;
}

export const useStore = create(
  devtools(
    persist<State>(
      (set, get) => ({
        //initial state
        allergenFilter: allergenFilter,
        bundles: [],
        categoryParams: categoryParams,
        printableRecipe: defaultPrintableRecipe,
        savedBlogs: [],
        savedProducts: [],
        savedRecipes: [],
        searchParams: defaultSearchParams,
        shopify: defaultShopify,
        visibility: defaultVisibility,

        /**
         * Created to avoid conflicts with the existing "shopify" property set above.
         * Use this to set cart data globally and merge with "shopify" property when going to checkout
         */
        newCartShopify: {
          attributes: [],
        },
        //setters
        openLoginDrawer: () =>
          set({
            visibility: { ...defaultVisibility, loginDrawerVisible: true },
          }),
        openCartDrawer: () =>
          set({
            visibility: { ...defaultVisibility, cartDrawerVisible: true },
          }),
        closeDrawer: () => set({ visibility: { ...defaultVisibility } }),
        setBundles: (bundles: Array<Bundle>) => set({ bundles }),
        setCategoryParams: (categoryParams: CategoryParams) => {
          set({ categoryParams });
        },
        setNewCartShopify: (newCartShopify: StoreNewCartShopify) =>
          set({ newCartShopify }),
        getCustomerToken: () => {
          return get().shopify?.customer?.token;
        },
        getCustomerId: () => {
          const shopifyId = get().shopify?.customer?.id;
          return shopifyId?.replace("gid://shopify/Customer/", "");
        },
      }),
      {
        name: determineBrand(), // unique name
        getStorage: () =>
          typeof window !== "undefined" ? localStorage : dummyStorage,
        // categoryParams is session-only navigation state for product breadcrumbs,
        // so never persist it or restore a previously persisted value
        partialize: state => ({ ...state, categoryParams }),
        merge: (persistedState, currentState) => ({
          ...currentState,
          ...(persistedState as Partial<State>),
          categoryParams: currentState.categoryParams,
        }),
      },
    ),
  ),
);
